import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const RadioTopSongsPreview = () => {
  const { t } = useLanguage();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [allSongs, setAllSongs] = useState([]);
  const [greekSongs, setGreekSongs] = useState([]);
  const [internationalSongs, setInternationalSongs] = useState([]);

  useEffect(() => {
    fetchRadioChart();
  }, []);

  const categorizeByLanguage = (songsArray) => {
    // Use backend categorization if available
    const greek = [];
    const international = [];
    
    songsArray.forEach(song => {
      if (song.category === 'greek') {
        greek.push(song);
      } else if (song.category === 'international') {
        international.push(song);
      }
      // Ignore songs without category (shouldn't happen with new uploads)
    });
    
    return { greek, international };
  };

  const fetchRadioChart = async () => {
    try {
      const backendUrl = getBackendUrl();
      // Add cache-busting parameter to force fresh data
      const response = await axios.get(
        `${backendUrl}/api/admin/radio-chart/latest?_t=${Date.now()}`,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.data.status === 'success') {
        const allSongsData = response.data.data.songs || [];
        setAllSongs(allSongsData);
        setSongs(allSongsData.slice(0, 10)); // Show top 10 by default
        
        // Categorize songs
        const { greek, international } = categorizeByLanguage(allSongsData);
        
        // ALWAYS limit to TOP 300 from each category
        const greekTop300 = greek.slice(0, 300);
        const internationalTop300 = international.slice(0, 300);
        
        setGreekSongs(greekTop300);
        setInternationalSongs(internationalTop300);
      } else {
        setError('Δεν βρέθηκαν δεδομένα Radio Top Songs');
      }
    } catch (err) {
      setError('Σφάλμα κατά τη φόρτωση των δεδομένων');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  if (error || songs.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-blue-800 mb-4">
            ⚠️ Δεν υπάρχουν διαθέσιμα δεδομένα Radio Chart αυτή τη στιγμή.
          </p>
          <p className="text-sm text-blue-600">
            Το chart ενημερώνεται καθημερινά από τον διαχειριστή.
          </p>
        </div>
      </div>
    );
  }

  // Show all available songs
  const previewSongs = songs;

  return (
    <div className="p-6">
      {/* Stats Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600">{t('totalSongs')}</p>
            <p className="text-2xl font-bold text-blue-600">{allSongs.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">{t('source')}</p>
            <p className="text-lg font-semibold text-gray-800">Radio Solutions Research</p>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-md p-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setActiveTab('all');
                setSongs(allSongs.slice(0, 10));
              }}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🎵 {t('allSongs')} ({allSongs.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('greek');
                setSongs(greekSongs.slice(0, 10));
              }}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                activeTab === 'greek'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🇬🇷 {t('greekSongs')} ({greekSongs.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('international');
                setSongs(internationalSongs.slice(0, 10));
              }}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                activeTab === 'international'
                  ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🌍 {t('internationalSongs')} ({internationalSongs.length})
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Features CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-sm text-gray-600 mb-1">
              {t('searchSongsArtists')}
            </p>
            <p className="text-xs text-gray-500">
              {t('browseChartsByWeek')}
            </p>
          </div>
          <Link to="/radio-top-songs">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-all duration-200 flex items-center gap-2 whitespace-nowrap">
              {t('discoverMore')}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 mb-6 max-h-[600px] overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-blue-600 to-purple-600">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                Rank
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                Change
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                Song
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                Artist(s)
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                Label
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                Plays
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                Plays evolution
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                DOC
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {previewSongs.map((song, index) => (
              <tr
                key={index}
                className={`hover:bg-blue-50 transition-colors ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                {/* Rank */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-white ${
                    song.rank <= 3 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                    song.rank <= 10 ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                    'bg-gray-500'
                  }`}>
                    {song.rank}
                  </span>
                </td>

                {/* Change */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {song.change !== 0 && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                      song.change > 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {song.change > 0 ? `↑ +${song.change}` : `↓ ${song.change}`}
                    </span>
                  )}
                  {song.change === 0 && (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>

                {/* Song */}
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900">{song.song}</p>
                </td>

                {/* Artist */}
                <td className="px-4 py-3">
                  <p className="text-sm text-gray-700">{song.artists}</p>
                </td>

                {/* Label */}
                <td className="px-4 py-3">
                  <p className="text-xs text-gray-500">{song.label || '—'}</p>
                </td>

                {/* Plays */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                    {song.plays || 0}
                  </span>
                </td>

                {/* Plays Evolution */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  {song.plays_evolution !== 0 && (
                    <span className={`text-sm font-semibold ${
                      song.plays_evolution > 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {song.plays_evolution > 0 ? `+${song.plays_evolution}` : song.plays_evolution}
                    </span>
                  )}
                  {song.plays_evolution === 0 && (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>

                {/* DOC (Days On Chart) */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold">
                    {song.days_on_chart}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 mb-6 max-h-[600px] overflow-y-auto">
        {previewSongs.map((song, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500"
          >
            {/* Rank Badge */}
            <div className="flex items-start justify-between mb-3">
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-lg ${
                song.rank <= 3 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                song.rank <= 10 ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                'bg-gray-500'
              }`}>
                #{song.rank}
              </span>

              {/* Change Badge */}
              {song.change !== 0 && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                  song.change > 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {song.change > 0 ? `↑ +${song.change}` : `↓ ${song.change}`}
                </span>
              )}
            </div>

            {/* Song Info */}
            <div className="mb-3">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {song.song}
              </h3>
              <p className="text-sm text-gray-600 mb-1">
                🎤 {song.artists}
              </p>
              {song.label && (
                <p className="text-xs text-gray-500">
                  🏢 {song.label}
                </p>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Plays</p>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                  {song.plays || 0}
                </span>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Plays Δ</p>
                <p className={`text-sm font-semibold ${
                  song.plays_evolution > 0 ? 'text-green-600' :
                  song.plays_evolution < 0 ? 'text-red-600' :
                  'text-gray-400'
                }`}>
                  {song.plays_evolution !== 0
                    ? (song.plays_evolution > 0 ? `+${song.plays_evolution}` : song.plays_evolution)
                    : '—'
                  }
                </p>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">DOC</p>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold">
                  {song.days_on_chart}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default RadioTopSongsPreview;
